import React from 'react';
import PropTypes from 'prop-types';

import { styled } from '@mui/material/styles';
import { Avatar, ListItemIcon, ListItemText, MenuItem, MenuList } from '@mui/material';

import { colors as themeColors } from '@themes/config/theme-colors';
import { typography } from '@themes/config/theme-typography';
import { navigate } from '@lib/utils/navigate';

import {
  HomeIcon,
  LibraryIcon,
  PDIcon,
  CommunityIcon,
  HelpIcon,
  CompetitionsIcon,
  WebAppIcon,
} from '@components/icons';

import { useAuth } from '@lib/auth';
import { useNav } from '@lib/hooks/index';
import { useFeatures } from '@lib/hooks/use-features';

const MainMenuContainer = styled(MenuList)(({ theme, ...props }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'left',
  paddingTop: '62px',
  height: '100%',
  overflowX: 'hidden',
  transition: '0.1s',
  color: themeColors.black['400'],

  '& .MuiListItemText-root': {
    display: props.open ? 'block' : 'none',
    transition: '0.2s',
  },
}));

const MainMenuItem = styled(MenuItem)(({ theme }) => ({
  height: '64px',
  minWidth: '69px',
  margin: '2px 20px 0 20px',
  borderRadius: '20px',

  '&.Mui-selected': {
    backgroundColor: themeColors.violet['main'],
    color: themeColors.white.main,

    '&:hover': {
      backgroundColor: themeColors.violet['900'],
    },
  },
}));

const MainMenuItemIcon = styled(ListItemIcon)(({ theme }) => ({
  width: '64px',
  paddingLeft: '8px',
  color: themeColors.black['400'],
  fill: themeColors.black['400'],
  '.Mui-selected &': {
    color: themeColors.white.main,
    fill: themeColors.white.main,
  },
}));

const MainMenuItemText = styled(ListItemText)(({ theme }) => ({
  ...typography.heading.xxs,
}));

/**
 * Steamhub menu
 * todo - Link with BE
 */
export const MainMenu = ({ open, closeHandler }) => {
  const { user } = useAuth();
  const { page } = useNav();
  const { isFeatureEnabled } = useFeatures();

  return (
    <MainMenuContainer open={open}>
      {/* DASHBOARD -------------------------------------------------------------------------------------------------*/}
      <MainMenuItem
        selected={page === 'dashboard'}
        onClick={() => {
          closeHandler();
          void navigate('/');
        }}
      >
        <MainMenuItemIcon>
          <HomeIcon />
        </MainMenuItemIcon>
        <MainMenuItemText>Dashboard</MainMenuItemText>
      </MainMenuItem>

      {/* LIBRARY ---------------------------------------------------------------------------------------------------*/}
      <MainMenuItem
        selected={page === 'library'}
        onClick={() => {
          closeHandler();
          void navigate('/r/library');
        }}
      >
        <MainMenuItemIcon>
          <LibraryIcon />
        </MainMenuItemIcon>
        <MainMenuItemText>Library</MainMenuItemText>
      </MainMenuItem>

      {/* PD & TUTORIALS --------------------------------------------------------------------------------------------*/}
      <MainMenuItem
        selected={page === 'pd'}
        onClick={() => {
          closeHandler();
          void navigate('/r/pd');
        }}
      >
        <MainMenuItemIcon>
          <PDIcon />
        </MainMenuItemIcon>
        <MainMenuItemText>PD & Tutorials</MainMenuItemText>
      </MainMenuItem>

      {/* COMMUNITY -------------------------------------------------------------------------------------------------*/}
      <MainMenuItem
        selected={page === 'community'}
        onClick={() => {
          closeHandler();
          void navigate('https://www.facebook.com/groups/wunderteacher/');
        }}
      >
        <MainMenuItemIcon>
          <CommunityIcon />
        </MainMenuItemIcon>
        <MainMenuItemText>Community</MainMenuItemText>
      </MainMenuItem>

      {/* HELP & SUPPORT --------------------------------------------------------------------------------------------*/}
      <MainMenuItem
        selected={page === 'help'}
        onClick={() => {
          closeHandler();
          void navigate('/p/help');
        }}
      >
        <MainMenuItemIcon>
          <HelpIcon />
        </MainMenuItemIcon>
        <MainMenuItemText>Help & Support</MainMenuItemText>
      </MainMenuItem>

      {/* COMPETITIONS ----------------------------------------------------------------------------------------------*/}
      <MainMenuItem
        selected={page === 'competitions'}
        onClick={() => {
          closeHandler();
          void navigate('/p/competition');
        }}
      >
        <MainMenuItemIcon>
          <CompetitionsIcon />
        </MainMenuItemIcon>
        <MainMenuItemText>Competitions</MainMenuItemText>
      </MainMenuItem>

      {/* WEBAPP ----------------------------------------------------------------------------------------------------*/}
      {isFeatureEnabled('steamhub.navigation.webapp') && (
        <MainMenuItem
          selected={page === 'webapp'}
          onClick={() => {
            closeHandler();
            void navigate('/webapp');
          }}
        >
          <MainMenuItemIcon>
            <WebAppIcon />
          </MainMenuItemIcon>
          <MainMenuItemText>Web App</MainMenuItemText>
        </MainMenuItem>
      )}

      {/* PROFILE ---------------------------------------------------------------------------------------------------*/}
      <MainMenuItem
        selected={page === 'profile'}
        sx={{ mt: '100px' }}
        onClick={() => {
          closeHandler();
          void navigate('/profile');
        }}
      >
        <MainMenuItemIcon>
          <Avatar sx={{ bgcolor: themeColors.violet.main, ml: '-8px' }}>
            {user?.username?.charAt(0).toUpperCase()}
          </Avatar>
        </MainMenuItemIcon>

        <MainMenuItemText>Profile & Settings</MainMenuItemText>
      </MainMenuItem>
    </MainMenuContainer>
  );
};

MainMenu.propTypes = {
  open: PropTypes.bool,
  closeHandler: PropTypes.func.isRequired,
};

MainMenu.defaultProps = {
  open: false,
};
